import { ENCODABLE_SCOPE_VALUES } from './serializer';
import {
  SEARCH_PREFERENCE_SCOPES,
  type SearchPreferenceScope,
  type RememberedScopePatch,
} from './rememberedScopePatch';

const INVALID = Symbol('invalid');

type FieldParser = (value: unknown) => unknown;

// `null` clears a tri-state field; every parser below accepts it, and the
// fields that have no unstated form are guarded by not being wrapped.
const clearable =
  (parse: FieldParser): FieldParser =>
  (value) =>
    value === null ? null : parse(value);

const oneOf =
  (allowed: readonly string[]): FieldParser =>
  (value) =>
    typeof value === 'string' && allowed.includes(value) ? value : INVALID;

const listOf =
  (allowed: readonly string[]): FieldParser =>
  (value) =>
    Array.isArray(value) &&
    value.every((item) => typeof item === 'string' && allowed.includes(item))
      ? value
      : INVALID;

const bool: FieldParser = (value) =>
  typeof value === 'boolean' ? value : INVALID;

const allowedUse = clearable(oneOf(ENCODABLE_SCOPE_VALUES.allowedUse));
const phraseProcessing = clearable(
  oneOf(ENCODABLE_SCOPE_VALUES.phraseProcessing)
);
const excludeNudity = clearable(bool);

// Every field a patch may name, and what it may say. A field absent from this
// table is rejected rather than ignored: an unrecognized name is far more
// likely to be a caller writing to the wrong place than a caller that wants a
// silent no-op, and the response would otherwise report success for a write
// that did not happen.
const SCOPE_FIELDS: Record<
  SearchPreferenceScope,
  Record<string, FieldParser>
> = {
  creativeImage: { allowedUse, phraseProcessing, excludeNudity },
  editorialImage: {
    editorialProducts: clearable(
      oneOf(ENCODABLE_SCOPE_VALUES.editorialProducts)
    ),
  },
  mixedImage: { license: listOf(ENCODABLE_SCOPE_VALUES.imageLicense) },
  creativeFilm: {
    license: listOf(ENCODABLE_SCOPE_VALUES.filmLicense),
    phraseProcessing,
  },
  editorialFilm: {
    offlineContent: clearable(oneOf(ENCODABLE_SCOPE_VALUES.offlineContent)),
  },
  mixedFilm: { license: listOf(ENCODABLE_SCOPE_VALUES.filmLicense) },
  analogFilm: { license: listOf(ENCODABLE_SCOPE_VALUES.filmLicense) },
  creativeImageFilm: { allowedUse, phraseProcessing, excludeNudity },
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export type RememberedScopePatchReadResult =
  | { patch: RememberedScopePatch }
  | { invalid: string };

type ScopeParseResult =
  | { fields: Record<string, unknown> }
  | { invalid: string };

const parseScope = (
  scope: SearchPreferenceScope,
  fields: Record<string, unknown>
): ScopeParseResult => {
  const parsers = SCOPE_FIELDS[scope];
  const parsed: Record<string, unknown> = {};

  for (const [field, value] of Object.entries(fields)) {
    const parse = parsers[field];
    if (!parse) return { invalid: `'${scope}' has no field '${field}'` };

    const result = parse(value);
    if (result === INVALID) {
      return { invalid: `invalid value for '${scope}.${field}'` };
    }

    parsed[field] = result;
  }

  return { fields: parsed };
};

/**
 * Reads an untrusted request body into a patch of the remembered scopes, or
 * says why it cannot.
 *
 * This is the read. `applyRememberedScopePatch` is what writes the result onto
 * the visitor's preferences, and it will not accept a body this has not read:
 * the two are separate because a body that fails is a response to send back,
 * not a preferences object.
 *
 * Validates against the stored form's code maps rather than against the enums,
 * so nothing can be accepted that the encoder would then silently drop — and
 * nothing a caller invents can reach the stored form, where a crafted value
 * would otherwise read back as a different preference entirely.
 */
export const readRememberedScopePatch = (
  value: unknown
): RememberedScopePatchReadResult => {
  if (!isObject(value)) return { invalid: 'body must be an object' };

  const patch: Record<string, Record<string, unknown>> = {};

  for (const [scope, fields] of Object.entries(value)) {
    if (!(SEARCH_PREFERENCE_SCOPES as readonly string[]).includes(scope)) {
      return { invalid: `unknown scope '${scope}'` };
    }
    if (!isObject(fields)) {
      return { invalid: `'${scope}' must be an object` };
    }

    const result = parseScope(scope as SearchPreferenceScope, fields);
    if ('invalid' in result) return result;

    patch[scope] = result.fields;
  }

  return { patch: patch as RememberedScopePatch };
};
